import { DateRange, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { BodyText, FlexColumn, Input, PageTile } from '@make-software/cspr-design';
import React, { useState, forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { CustomSettings } from './custom-settings';
import { toYMD } from 'app/components/utils/date-time';
import { CalendarDateRange } from 'app/console-api/statistics/types';
// @ts-ignore
import { UTCDate } from '@date-fns/utc';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { DateRangeFilterAdapter, defaultRange } from 'app/components/filters/date-range-filter';
import { FlexColumnProps } from '@make-software/cspr-design/dist/lib/components/flex-column/flex-column';

const CalendarContentContainer = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    padding: '8px 16px',
    margin: '0px 0'
  })
);

const CalendarContent = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'column', 'row', 'row'],
    width: '100%',
    justifyContent: ['start', 'start', 'space-between', 'space-between'],
    '& > * + *': {
      marginTop: [8, 8, 0],
      marginLeft: [0, 0, 8]
    }
  })
);

const DayPickerWrapper = styled.div(({ theme }) =>
  theme.withMedia({
    '.rdp-root': {
      '--rdp-accent-color': theme.styleguideColors.contentLightBlue,
      '--rdp-accent-background-color': `${theme.styleguideColors.contentLightBlue}26`,
      '.rdp-button_previous, .rdp-button_next': {
        backgroundColor: theme.styleguideColors.fillSecondary,
        borderRadius: '50%',
        marginLeft: 10,
        svg: {
          height: '16px',
          fill: 'black'
        }
      }
    }
  })
);

export interface CalendarRef {
  resetState: () => void;
}

export const Calendar = forwardRef<
  CalendarRef,
  FlexColumnProps & React.RefAttributes<HTMLDivElement>
>((props, ref) => {
  const filter = useUrlFilterController();
  const urlRange = filter.get(DateRangeFilterAdapter) as CalendarDateRange;
  const [localRange, setLocalRange] = useState<CalendarDateRange>(() => urlRange ?? defaultRange());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const inputValue = useMemo(() => {
    return `${toYMD(localRange.from)} - ${toYMD(localRange.to)}`;
  }, [localRange]);

  const MS_IN_DAY = 24 * 60 * 60 * 1000;

  const utcToLocalDay = (d: Date): Date => {
    // Take UTC Y/M/D and create a local date at midnight for that day
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  const nowUtc = new UTCDate();
  // 90 days back in UTC
  const dateLimitUtc = new UTCDate(nowUtc.getTime() - 90 * MS_IN_DAY);

  // Convert your UTC day boundaries into local day objects for DayPicker
  const maxDayLocal = utcToLocalDay(nowUtc); // last selectable date (today UTC)
  const minDayLocal = utcToLocalDay(dateLimitUtc); // earliest selectable date

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) return;

    const from = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate());
    const to = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate());

    setLocalRange({ from, to });
  };

  const applyRange = (from: Date, to: Date) => {
    filter.set(DateRangeFilterAdapter, { from, to });
    handleRangeSelect({ from, to });
  };

  return (
    <FlexColumn {...props}>
      <Input
        value={inputValue}
        label={<BodyText size={1}>Date UTC</BodyText>}
        disabled={false}
        placeholder="Select Date Range"
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
        onChange={() => {}}
      />
      {isCalendarOpen && (
        <CalendarContentContainer>
          <CalendarContent>
            <DayPickerWrapper>
              <DayPicker
                mode="range"
                selected={localRange}
                onSelect={handleRangeSelect}
                required
                // disable future days and anything before min
                disabled={[{ after: maxDayLocal }, { before: minDayLocal }]}
                excludeDisabled
              />
            </DayPickerWrapper>

            <CustomSettings
              dateRange={localRange}
              onApply={applyRange}
              onDateRangeChange={handleRangeSelect}
              onCloseAction={() => setIsCalendarOpen(false)}
            />
          </CalendarContent>
        </CalendarContentContainer>
      )}
    </FlexColumn>
  );
});
